import {
    Card,
    CardContent,
    Box,
    Typography,
    Chip,
    IconButton,
    Divider,
    Autocomplete,
    Tooltip,
    Select,
    Option,
    Badge,
} from "@mui/joy";
import { Minus, Plus, Trash2, CircleAlert } from "lucide-react";
import { motion } from "framer-motion";

interface ProductCardProps {
    categoryName: string;
    units?: string;
    
    // Product Pick
    productOptions: string[];
    selectedProduct: string | null;
    onProductChange: (val: any) => void;
    
    // Color Pick
    colorOptions: string[];
    selectedColor: string | null;
    onColorChange: (val: string | null) => void;
    
    // Quantity
    quantity: number | string;
    onQuantityUpdate: (direction: "add" | "remove") => void;
    
    // Display
    priceDisplay: string;
    priceLabel: string;
    
    // Alerting
    hasIssue?: boolean;
    issueTitle?: string;
    
    // Chips
    dependents?: { name: string; isSatisfied: boolean }[];
    
    onRemove: () => void;
}

/**
 * Pure UI Product Card
 * No calculations, no side-effects. Just renders what its told.
 */
export function ProductCard(props: ProductCardProps) {
    const {
        categoryName,
        units,
        productOptions,
        selectedProduct,
        onProductChange,
        colorOptions,
        selectedColor,
        onColorChange,
        quantity,
        onQuantityUpdate,
        priceDisplay,
        priceLabel,
        hasIssue,
        issueTitle,
        dependents,
        onRemove,
    } = props;

    return (
        <Card
            sx={{
                p: 1.5,
                px: 2,
                borderRadius: 10,
                border: "1px solid",
                borderColor: "divider",
                zIndex: 2,
            }}
            variant="soft"
        >
            <CardContent>
                <Box display="flex" justifyContent="space-between" gap={3}>
                    {/* LEFT SECTION: Info & Pickers */}
                    <Box flex={1} display="flex" flexDirection="column" gap={0.5}>
                        <Box display="flex" alignItems="center" gap={1}>
                            <Typography level="body-sm" sx={{ fontWeight: 500 }}>
                                {categoryName}
                            </Typography>
                            {units && <Typography level="body-xs">({units})</Typography>}
                            
                            {hasIssue && (
                                <Tooltip title={issueTitle || "Issue detected"} arrow variant="solid" color="warning">
                                    <motion.div
                                        animate={{ opacity: [1, 0.5, 1], scale: [1, 1.1, 1] }}
                                        transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
                                        style={{ display: "flex", alignItems: "center" }}
                                    >
                                        <CircleAlert size={18} color="#ed6c02" />
                                    </motion.div>
                                </Tooltip>
                            )}
                            
                            <Box display="flex" gap={0.5}>
                                {dependents?.map((dep) => (
                                    <Chip
                                        key={dep.name}
                                        size="sm"
                                        sx={{ borderRadius: 6, border: "1px solid", borderColor: "divider" }}
                                        color={dep.isSatisfied ? "success" : "danger"}
                                    >
                                        {dep.name}
                                    </Chip>
                                ))}
                            </Box>
                        </Box>

                        {/* Pickers */}
                        <Box display="flex" flexDirection="column" gap={0.5}>
                            <Autocomplete
                                value={selectedProduct}
                                variant="outlined"
                                sx={{ maxWidth: "300px" }}
                                placeholder="Select"
                                options={productOptions}
                                onChange={(_event, value) => onProductChange(value)}
                                popupIcon={null}
                                clearIcon={null}
                                slotProps={{
                                    root: {
                                        sx: {
                                            border: "none",
                                            background: "transparent",
                                            padding: 0,
                                            py: 0.5,
                                            minHeight: "auto",
                                            fontSize: "inherit",
                                            boxShadow: "none",
                                            outline: "none",
                                            "&:focus-within": { outline: "none", boxShadow: "none" },
                                            "&::before": { display: "none" },
                                            "&::after": { display: "none" },
                                        },
                                    },
                                    input: { sx: { fontWeight: 500 } },
                                }}
                            />

                            {selectedProduct && colorOptions.length > 0 && (
                                <Select
                                    size="sm"
                                    placeholder="Select color"
                                    value={selectedColor || ""}
                                    onChange={(_e, v) => onColorChange(v as string)}
                                    sx={{ maxWidth: 240, mt: 0.25 }}
                                >
                                    {colorOptions.map((opt) => (
                                        <Option key={opt} value={opt}>{opt}</Option>
                                    ))}
                                </Select>
                            )}
                        </Box>
                    </Box>

                    {/* MIDDLE SECTION: Quantity */}
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Box sx={{ display: "flex", borderRadius: "40px", p: 0.2, pb: 0.15, alignItems: "center", gap: 1, border: "1px solid", borderColor: "divider" }}>
                            <IconButton variant="soft" size="sm" color="danger" onClick={() => onQuantityUpdate("remove")} sx={{ border: "1px solid", borderColor: "divider", "--IconButton-size": "32px", borderRadius: "50%" }}>
                                <Minus size={12} />
                            </IconButton>

                            <Typography level="body-sm" sx={{ fontWeight: "md", mb: 0 }} component="span">
                                {quantity}
                            </Typography>
                            
                            <Badge badgeContent={quantity} variant="soft" color="warning">
                                <IconButton variant="soft" color="success" size="sm" onClick={() => onQuantityUpdate("add")} sx={{ border: "1px solid", borderColor: "divider", "--IconButton-size": "32px", borderRadius: "50%" }}>
                                    <Plus size={12} />
                                </IconButton>
                            </Badge>
                        </Box>
                    </Box>

                    <Divider orientation="vertical" />

                    {/* RIGHT SECTION: Price */}
                    <Box display="flex" flexDirection="column" justifyContent="center" minWidth={140}>
                        <Typography sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                            {priceDisplay}
                        </Typography>
                        <Typography level="body-xs">
                            {priceLabel}
                        </Typography>
                    </Box>

                    <Divider orientation="vertical" />

                    {/* ACTIONS */}
                    <Box display="flex" alignItems="center" gap={3}>
                        <IconButton size="sm" color="danger" variant="soft" onClick={onRemove} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 6, "--IconButton-size": "26px" }}>
                            <Trash2 size={12} />
                        </IconButton>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
}

export default ProductCard;
